import Vue from 'vue';
import { defineStore } from 'pinia';
import _ from 'lodash';
import echo from '@/echo';

import PointOfSaleApi from '@/extensions/shop/pointOfSale/store/api/pointOfSale';
import PointOfSalePurchaseApi from '@/extensions/shop/pointOfSale/store/api/purchase';
import PurchaseApi from '@/extensions/shop/store/api/purchase';
import StatusApi from '@/extensions/shop/store/api/purchaseStatus';
import StockApi from '@/extensions/shop/store/api/stock';
import PaymentMethodApi from '@/extensions/shop/store/api/paymentMethod';
import ShippingPackageApi from '@/extensions/shop/store/api/shippingPackage';
import TaxApi from '@/extensions/shop/store/api/tax';

import { checkRestrictions } from '@/helpers';
import { Notification } from 'element-ui';

import i18n from '@/i18n';
import pinia from '@/pinia';
import store from '@/store';
import { useAuthStore, usePosCartStore, useSetupStore } from '@/stores';

export const usePosSetupStore = defineStore('posSetup', {
    state: () => ({
        loading: true,
        initialized: false,
        autofocusSearchInput: true,
        activePointOfSale: {},
        context: "",
        activeStock: {},
        showOnlyOwnPurchasesOnHold: true,
        numberOfPurchasesOnHold: 0,
        purchasesOnHold: {
            loading: false,
            items: []
        },
        numberOfPendingArticleReservations: 0,
        pendingReservationPurchases: {
            loading: false,
            items: []
        },
        numberOfPendingArticleShippings: 0,
        pendingShippingPurchases: {
            loading: false,
            items: []
        },
        options: {
            pointOfSale: {
                loading: false,
                items: []
            },
            cashier: {
                loading: false,
                items: []
            },
            stock: {
                loading: false,
                items: []
            },
            status: {
                loading: false,
                items: []
            },
            paymentMethod: {
                loading: false,
                items: []
            },
            tax: {
                loading: false,
                items: []
            },
        },
        permittedStocks: []
    }),

    getters: {
        loadingPurchasesOnHold: (state) => state.purchasesOnHold.loading,
        purchasesOnHoldItems: (state) => state.purchasesOnHold.items,
        loadingPendingReservationPurchases: (state) => state.pendingReservationPurchases.loading,
        pendingReservationPurchasesItems: (state) => state.pendingReservationPurchases.items,
        loadingPendingShippingPurchases: (state) => state.pendingShippingPurchases.loading,
        pendingShippingPurchasesItems: (state) => state.pendingShippingPurchases.items,

        activeRegularTax: (state) => {
            const setupStore = useSetupStore();
            const posCountry = _.get(state, 'activePointOfSale.address.country');
            const shopCountry = _.get(setupStore, 'activeCms.shop.address.country')
                || _.get(setupStore, 'activeCms.address.country');
            if (posCountry && posCountry !== shopCountry) {
                return state.options.tax.items.find(tax => {
                    return tax.default === false
                        && tax.reduced === false
                        && tax.country === posCountry;
                });
            }
            return undefined;
        },

        activeReducedTax: (state) => {
            const setupStore = useSetupStore();
            const posCountry = _.get(state, 'activePointOfSale.address.country');
            const shopCountry = _.get(setupStore, 'activeCms.shop.address.country')
                || _.get(setupStore, 'activeCms.address.country');
            if (posCountry && posCountry !== shopCountry) {
                return state.options.tax.items.find(tax => {
                    return tax.default === false
                        && tax.reduced === true
                        && tax.country === posCountry;
                });
            }
            return undefined;
        },

        currency() {
            const setupStore = useSetupStore();
            const features = _.get(setupStore, 'activeCms.config.template.features') || [];
            const customerPrices = !!features.find(el => el === 'pos:customer-prices');

            let currency;
            if (customerPrices) {
                const purchaser = usePosCartStore(pinia).purchaser;
                currency = _.get(purchaser, 'customer.customerGroup.currency');
            }

            return currency
                || _.get(this.activePointOfSale, 'customerGroup.currency')
                || _.get(setupStore, 'activeCms.shop.defaultCustomerGroup.currency');
        }
    },

    actions: {
        // Mutation replacements (direct state modifications)
        updateLoading(value) {
            this.loading = value;
        },

        updateInitialized(value) {
            this.initialized = value;
        },

        updateAutofocusSearchInput(value) {
            this.autofocusSearchInput = value;
        },

        updateShowOnlyOwnPurchasesOnHold(value) {
            this.showOnlyOwnPurchasesOnHold = value;
        },

        updateNumberOfPurchasesOnHold(value) {
            this.numberOfPurchasesOnHold = value;
        },

        updatePurchasesOnHoldLoading(value) {
            this.purchasesOnHold.loading = value;
        },

        updatePurchasesOnHold(value) {
            this.purchasesOnHold.items = value;
        },

        updateNumberOfPendingArticleReservations(value) {
            this.numberOfPendingArticleReservations = value;
        },

        updatePendingReservationPurchasesLoading(value) {
            this.pendingReservationPurchases.loading = value;
        },

        updatePendingReservationPurchaseItemLoading({ item, value }) {
            let purchaseIndex, itemIndex;
            purchaseIndex = this.pendingReservationPurchases.items.findIndex(purchase => {
                itemIndex = purchase.items.findIndex(el => el.id === item.id);
                return itemIndex >= 0;
            });

            if (purchaseIndex >= 0 && itemIndex >= 0) {
                if (value) {
                    Vue.set(this.pendingReservationPurchases.items[purchaseIndex].items[itemIndex], 'loading', value);
                } else {
                    Vue.delete(this.pendingReservationPurchases.items[purchaseIndex].items[itemIndex], 'loading');
                }
            }
        },

        updatePendingReservationPurchaseItemReserving({ item, value }) {
            let purchaseIndex, itemIndex;
            purchaseIndex = this.pendingReservationPurchases.items.findIndex(purchase => {
                itemIndex = purchase.items.findIndex(el => el.id === item.id);
                return itemIndex >= 0;
            });

            if (purchaseIndex >= 0 && itemIndex >= 0) {
                if (value) {
                    Vue.set(this.pendingReservationPurchases.items[purchaseIndex].items[itemIndex], 'reserving', value);
                } else {
                    Vue.delete(this.pendingReservationPurchases.items[purchaseIndex].items[itemIndex], 'reserving');
                }
            }
        },

        updatePendingReservationPurchaseItemUnreserving({ item, value }) {
            let purchaseIndex, itemIndex;
            purchaseIndex = this.pendingReservationPurchases.items.findIndex(purchase => {
                itemIndex = purchase.items.findIndex(el => el.id === item.id);
                return itemIndex >= 0;
            });

            if (purchaseIndex >= 0 && itemIndex >= 0) {
                if (value) {
                    Vue.set(this.pendingReservationPurchases.items[purchaseIndex].items[itemIndex], 'unreserving', value);
                } else {
                    Vue.delete(this.pendingReservationPurchases.items[purchaseIndex].items[itemIndex], 'unreserving');
                }
            }
        },

        updatePendingReservationPurchaseItemMissing({ item, value }) {
            let purchaseIndex, itemIndex;
            purchaseIndex = this.pendingReservationPurchases.items.findIndex(purchase => {
                itemIndex = purchase.items.findIndex(el => el.id === item.id);
                return itemIndex >= 0;
            });

            if (purchaseIndex >= 0 && itemIndex >= 0) {
                if (value) {
                    Vue.set(this.pendingReservationPurchases.items[purchaseIndex].items[itemIndex], 'missing', value);
                } else {
                    Vue.delete(this.pendingReservationPurchases.items[purchaseIndex].items[itemIndex], 'missing');
                }
            }
        },

        updatePendingReservationPurchaseItemUnmissing({ item, value }) {
            let purchaseIndex, itemIndex;
            purchaseIndex = this.pendingReservationPurchases.items.findIndex(purchase => {
                itemIndex = purchase.items.findIndex(el => el.id === item.id);
                return itemIndex >= 0;
            });

            if (purchaseIndex >= 0 && itemIndex >= 0) {
                if (value) {
                    Vue.set(this.pendingReservationPurchases.items[purchaseIndex].items[itemIndex], 'missing', value);
                } else {
                    Vue.delete(this.pendingReservationPurchases.items[purchaseIndex].items[itemIndex], 'missing');
                }
            }
        },

        updatePendingReservationPurchaseItem(item) {
            let purchaseIndex, itemIndex;
            purchaseIndex = this.pendingReservationPurchases.items.findIndex(purchase => {
                itemIndex = purchase.items.findIndex(el => el.id === item.id);
                return itemIndex >= 0;
            });

            if (purchaseIndex >= 0 && itemIndex >= 0) {
                Vue.set(this.pendingReservationPurchases.items[purchaseIndex].items, itemIndex, item);
            }
        },

        updatePendingReservationPurchases(value) {
            this.pendingReservationPurchases.items = value;
        },

        updateNumberOfPendingArticleShippings(value) {
            this.numberOfPendingArticleShippings = value;
        },

        updatePendingShippingPurchasesLoading(value) {
            this.pendingShippingPurchases.loading = value;
        },

        updatePendingShippingPurchaseLoading({ purchase, value }) {
            const purchaseIndex = this.pendingShippingPurchases.items.findIndex(el => el.id === purchase.id);

            if (purchaseIndex >= 0) {
                if (value) {
                    Vue.set(this.pendingShippingPurchases.items[purchaseIndex], 'loading', value);
                } else {
                    Vue.delete(this.pendingShippingPurchases.items[purchaseIndex], 'loading');
                }
            }
        },

        updatePendingShippingPurchase(purchase) {
            const purchaseIndex = this.pendingShippingPurchases.items.findIndex(el => el.id === purchase.id);
            if (purchaseIndex >= 0) {
                Vue.set(this.pendingShippingPurchases.items, purchaseIndex, purchase);
            }
        },

        updatePendingShippingPurchases(value) {
            this.pendingShippingPurchases.items = value;
        },

        setActivePointOfSale(pointOfSale) {
            this.activePointOfSale = pointOfSale;
            PointOfSaleApi.setActivePointOfSaleId(pointOfSale.id);

            // set first stock as active one
            if (pointOfSale.stocks && pointOfSale.stocks.length) {
                this.activeStock = pointOfSale.stocks[0];
            }
        },

        updatePointOfSaleOptionsLoading(value) {
            this.options.pointOfSale.loading = value;
        },

        insertPointOfSaleOptions(pointsOfSale) {
            this.options.pointOfSale.items = pointsOfSale;
        },

        updateTaxOptionsLoading(value) {
            this.options.tax.loading = value;
        },

        insertTaxOptions(taxes) {
            this.options.tax.items = taxes;
        },

        updateStockOptionsLoading(value) {
            this.options.stock.loading = value;
        },

        insertStockOptions(stocks) {
            this.options.stock.items = stocks;
        },

        setActiveStock(stock) {
            this.activeStock = stock;
            localStorage.setItem('active-stock-id', stock.id);
        },

        updatePaymentMethodOptionsLoading(value) {
            this.options.paymentMethod.loading = value;
        },

        insertPaymentMethodOptions(paymentMethods) {
            this.options.paymentMethod.items = paymentMethods;
        },

        updateStatusOptionsLoading(value) {
            this.options.status.loading = value;
        },

        insertStatusOptions(items) {
            this.options.status.items = items;
        },

        insertPermittedStocks(stocks) {
            this.permittedStocks = stocks;
        },

        // Async actions
        async initialize(context) {
            const setupStore = useSetupStore();
            this.updateLoading(true);
            this.updateInitialized(false);
            this.updateNumberOfPurchasesOnHold(0);
            this.updateNumberOfPendingArticleReservations(0);
            this.updateNumberOfPendingArticleShippings(0);

            const shopId = _.get(setupStore, 'activeCms.shop.id');
            // Multistore: the register belongs to one store and checkout only
            // accepts that store's payment methods.
            const cmsId = _.get(setupStore, 'activeCms.id');

            const promises = [
                this.fetchTaxOptions(shopId),
                this.fetchStockOptions(shopId),
                this.fetchPaymentMethodOptions(shopId, cmsId),
                this.fetchStatusOptions(shopId),
                this.fetchPointOfSaleOptions(shopId)
                    .then(() => {
                        if (context === 'SALES') {
                            let stocks = [];

                            const authStore = useAuthStore();
                            const isOwner = authStore.isOwner;
                            if (isOwner) {
                                stocks = this.options.stock.items;
                            } else if (checkRestrictions(['ex:shop', 'ex:shop:stocks'])) {
                                const right = authStore.administrator.administratorRights.find(
                                    r => r.key === 'ex:shop:stocks' && r.permission.access
                                );
                                if (right && right.permission && right.permission.access) {
                                    if (right.permission.any === true) {
                                        stocks = this.options.stock.items;
                                    } else {
                                        stocks = right.permission.stocks;
                                    }
                                    this.insertPermittedStocks(stocks);
                                }
                            }
                            if (stocks.length) {
                                let activeStock;

                                const activeStockId = _.get(this.activeStock, 'id') || PointOfSaleApi.getActiveStockId();
                                if (activeStockId) {
                                    activeStock = stocks.find(stock => parseInt(stock.id) === parseInt(activeStockId));
                                }
                                if (!activeStock) activeStock = _.head(stocks);

                                this.updateActiveStock(activeStock);
                            }
                        } else if (context === 'POS') {
                            const pointsOfSale = this.options.pointOfSale.items;

                            if (pointsOfSale.length) {
                                let activePointOfSale;
                                const activePointOfSaleId = _.get(this.activePointOfSale, 'id') || PointOfSaleApi.getActivePointOfSaleId();
                                if (activePointOfSaleId) {
                                    activePointOfSale = pointsOfSale.find(
                                        pos => parseInt(pos.id) === parseInt(PointOfSaleApi.getActivePointOfSaleId())
                                    );
                                }

                                if (!activePointOfSale) activePointOfSale = _.head(pointsOfSale);
                                this.updateActivePointOfSale(activePointOfSale);
                            }
                        }
                    }),
            ];

            return Promise.all(promises)
                .then(() => this.updateInitialized(true))
                .finally(() => this.updateLoading(false));
        },

        updateActivePointOfSale(pointOfSale) {
            const setupStore = useSetupStore();
            // stop listening
            let id = this.activePointOfSale.id;
            if (id) {
                echo.client.private(`point-of-sale.${id}`)
                    .stopListening('.purchase-created')
                    .stopListening('.purchase-updated')
                    .stopListening('.purchase-payed')
                    .stopListening('.purchase-canceled');

                const shopID = _.get(setupStore, 'activeCms.shop.id');
                echo.client.private(`shop.${shopID}`)
                    .stopListening('.purchase-created')
                    .stopListening('.purchase-updated')
                    .stopListening('.purchase-payed')
                    .stopListening('.purchase-canceled');

                // TODO listen for event in relation to logged in user
                const stocks = this.activePointOfSale.stocks || [];
                stocks.forEach((stock) => {
                    echo.client.private('stock.' + stock.id)
                        .stopListening('.articlestock-report-created');
                });
            }

            this.setActivePointOfSale(pointOfSale);

            // listen to broadcasted pos events
            id = pointOfSale.id;
            if (id) {
                const fetchDependencies = (refetching = false) => {
                    // console.log('>>> refetching', refetching)
                    // this.fetchPurchasesOnHold()
                    this.fetchNumberOfPendingArticleReservations({ loading: false, pointOfSaleID: id });
                    this.fetchPendingReservationPurchases({ loading: false, pointOfSaleID: id, skip: 0, take: 10 });
                    this.fetchNumberOfPendingArticleShippings({ loading: false, pointOfSaleID: id });
                    this.fetchPendingShippingPurchases({ loading: false, pointOfSaleID: id, skip: 0, take: 10 });
                    usePosCartStore(pinia).load();
                };
                const debouncedRefetch = _.debounce(fetchDependencies, 100);
                fetchDependencies();

                echo.client.private(`point-of-sale.${id}`)
                    .listen('.purchase-created', purchase => {
                        console.log('created', purchase);
                        debouncedRefetch(true);
                    })
                    .listen('.purchase-updated', purchase => {
                        console.log('updated', purchase);
                        debouncedRefetch(true);
                    })
                    .listen('.purchase-payed', purchase => {
                        console.log('payed', purchase);
                        debouncedRefetch(true);
                    })
                    .listen('.purchase-canceled', purchase => {
                        console.log('canceled', purchase);
                        debouncedRefetch(true);
                    });

                const shopID = _.get(setupStore, 'activeCms.shop.id');
                const customer = _.get(usePosCartStore(pinia).purchaser, 'customer');
                echo.client.private(`shop.${shopID}`)
                    .listen('.purchase-created', purchase => {
                        // console.log('created', purchase)
                        if (!this.showOnlyOwnPurchasesOnHold && !customer) {
                            this.fetchPurchasesOnHold({ loading: false });
                        }
                    })
                    .listen('.purchase-updated', purchase => {
                        // console.log('updated', purchase)
                        if (!this.showOnlyOwnPurchasesOnHold && !customer) {
                            this.fetchPurchasesOnHold({ loading: false });
                        }
                    })
                    .listen('.purchase-payed', purchase => {
                        // console.log('payed', purchase)
                        if (!this.showOnlyOwnPurchasesOnHold && !customer) {
                            this.fetchPurchasesOnHold({ loading: false });
                        }
                    })
                    .listen('.purchase-canceled', purchase => {
                        // console.log('canceled', purchase)
                        if (!this.showOnlyOwnPurchasesOnHold && !customer) {
                            this.fetchPurchasesOnHold({ loading: false });
                        }
                    });

                const stocks = this.activePointOfSale.stocks || [];
                stocks.forEach((stock) => {
                    echo.client.private('stock.' + stock.id)
                        .listen('.articlestock-report-created', () => {
                            Notification.success({
                                title: i18n.t('pdf.stockReportCreated'),
                                iconClass: 'fas el-icon-fa-check',
                                position: 'bottom-left',
                                customClass: 'dark',
                                duration: 8000,
                            });
                        });
                });
            }
        },

        updateActiveStock(stock) {
            // stop listening
            let id = this.activeStock.id;
            if (id) {
                echo.client.private(`stock.${id}`)
                    .stopListening('.purchase-created')
                    .stopListening('.purchase-updated')
                    .stopListening('.purchase-payed')
                    .stopListening('.purchase-canceled');
            }

            this.setActiveStock(stock);

            // listen to broadcasted pos events
            id = stock.id;
            if (id) {
                const fetchDependencies = () => {
                    // this.fetchPurchasesOnHold()
                    this.fetchNumberOfPendingArticleReservations({ loading: false, stockID: id });
                    this.fetchPendingReservationPurchases({ loading: false, pointOfSaleID: null, stockID: id, skip: 0, take: 10 });
                    this.fetchNumberOfPendingArticleShippings({ loading: false, stockID: id });
                    this.fetchPendingShippingPurchases({ loading: false, stockID: id, skip: 0, take: 10 });
                    usePosCartStore(pinia).load();
                };
                const debouncedRefetch = _.debounce(fetchDependencies, 100);
                fetchDependencies();

                echo.client.private(`stock.${id}`)
                    .listen('.purchase-created', purchase => {
                        console.log('created', purchase);
                        debouncedRefetch();
                    })
                    .listen('.purchase-updated', purchase => {
                        console.log('updated', purchase);
                        debouncedRefetch();
                    })
                    .listen('.purchase-payed', purchase => {
                        console.log('payed', purchase);
                        debouncedRefetch();
                    })
                    .listen('.purchase-canceled', purchase => {
                        console.log('canceled', purchase);
                        debouncedRefetch();
                    });
            }
        },

        fetchPointOfSaleOptions(shopID) {
            this.updatePointOfSaleOptionsLoading(true);
            return PointOfSaleApi.fetchList(shopID)
                .then(pointsOfSale => {
                    this.updatePointOfSaleOptionsLoading(false);
                    this.insertPointOfSaleOptions(pointsOfSale);
                })
                .catch(err => {
                    this.updatePointOfSaleOptionsLoading(false);
                    throw err;
                });
        },

        fetchTaxOptions(shopID) {
            const setupStore = useSetupStore();
            if (_.has(setupStore, 'activeShop.defaultTax')) {
                this.insertTaxOptions([setupStore.activeShop.defaultTax]);
            }

            this.updateTaxOptionsLoading(true);
            return TaxApi.fetchList(shopID)
                .then(taxes => {
                    this.updateTaxOptionsLoading(false);
                    this.insertTaxOptions(taxes);
                })
                .catch(err => {
                    this.updateTaxOptionsLoading(false);
                    throw err;
                });
        },

        fetchStockOptions(shopID) {
            this.updateStockOptionsLoading(true);
            return StockApi.fetchList(shopID)
                .then(stocks => {
                    this.updateStockOptionsLoading(false);
                    this.insertStockOptions(stocks);
                })
                .catch(err => {
                    this.updateStockOptionsLoading(false);
                    throw err;
                });
        },

        fetchPaymentMethodOptions(shopID, cmsID) {
            this.updatePaymentMethodOptionsLoading(true);
            return PaymentMethodApi.fetchList({
                shop: parseInt(shopID),
                cms: cmsID ? parseInt(cmsID) : null,
                point_of_sale_flag: true,
            })
                .then(paymentMethods => {
                    this.updatePaymentMethodOptionsLoading(false);
                    this.insertPaymentMethodOptions(paymentMethods);
                })
                .catch(err => {
                    this.updatePaymentMethodOptionsLoading(false);
                    throw err;
                });
        },

        fetchStatusOptions() {
            this.updateStatusOptionsLoading(true);
            return StatusApi.fetchList()
                .then(statuses => {
                    this.updateStatusOptionsLoading(false);
                    this.insertStatusOptions(statuses);
                })
                .catch(err => {
                    this.updateStatusOptionsLoading(false);
                    throw err;
                });
        },

        fetchPurchasesOnHold(data) {
            const setupStore = useSetupStore();
            if (!data) data = {};
            let { loading, skip, take } = data;

            if (loading !== false) this.updatePurchasesOnHoldLoading(true);

            if (!take) take = 10;
            if (!skip) skip = 0;

            let currentPage = 1;
            if (skip > 0) currentPage = Math.floor(skip / take) + 1;

            const filters = [
                {
                    key: 'status_keys',
                    in: ['on-hold'],
                }
            ];

            const customerID = _.get(usePosCartStore(pinia).purchaser, 'customer.id');
            if (customerID != null) {
                filters.push({
                    key: 'customer',
                    match: customerID,
                });
            } else {
                const shopID = _.get(setupStore, 'activeCms.shop.id');
                const pointOfSaleID = _.get(this, 'activePointOfSale.id');

                if (this.showOnlyOwnPurchasesOnHold && pointOfSaleID) {
                    filters.push({
                        key: 'point_of_sale',
                        match: pointOfSaleID,
                    });
                } else if (shopID) {
                    filters.push({
                        key: 'shop',
                        match: shopID,
                    });
                }
            }

            const payload = {
                currentPage,
                perPage: take,
                includeWebShopPurchases: false,
                includeInternals: false,
                includePointOfSalePurchases: true,
                includeItems: true,
                includeImages: true,
                includeCoupons: true,
                filters
            };

            return PurchaseApi.fetchPurchasesPaginated(payload, 'no-cache')
                .then(responseData => {
                    this.updateNumberOfPurchasesOnHold(responseData.pageInfo.total);
                    this.updatePurchasesOnHold(responseData.items);
                })
                .catch(err => {
                    this.updateNumberOfPurchasesOnHold(0);
                    this.updatePurchasesOnHold([]);

                    console.error(err);
                })
                .finally(() => {
                    if (loading !== false) this.updatePurchasesOnHoldLoading(false);
                });
        },

        fetchNumberOfPendingArticleReservations({ pointOfSaleID, stockID }) {
            return PointOfSalePurchaseApi.fetchNumberOfPendingArticleReservations(pointOfSaleID, stockID, _.get(useSetupStore(pinia), 'activeCms.id', null))
                .then(value => {
                    this.updateNumberOfPendingArticleReservations(value);
                })
                .catch(err => {
                    throw err;
                });
        },

        fetchPendingReservationPurchases({ loading, pointOfSaleID, stockID, skip, take }) {
            if (loading !== false) this.updatePendingReservationPurchasesLoading(true);
            return PointOfSalePurchaseApi.fetchPendingReservationPurchases(pointOfSaleID, stockID, skip, take, _.get(useSetupStore(pinia), 'activeCms.id', null))
                .then(value => {
                    this.updatePendingReservationPurchases(value);
                })
                .catch(err => {
                    throw err;
                })
                .finally(() => {
                    if (loading !== false) this.updatePendingReservationPurchasesLoading(false);
                });
        },

        markPurchasedItemAsReserved({ item, stock, pivotID }) {
            this.updatePendingReservationPurchaseItemLoading({ item, value: true });
            this.updatePendingReservationPurchaseItemReserving({ item, value: true });

            return PointOfSalePurchaseApi.markPurchasedItemAsReserved(item, stock, this.activePointOfSale, pivotID)
                .then(value => {
                    this.updatePendingReservationPurchaseItem(value);
                })
                .catch(err => {
                    throw err;
                })
                .finally(() => {
                    this.updatePendingReservationPurchaseItemLoading({ item, value: false });
                    this.updatePendingReservationPurchaseItemReserving({ item, value: false });
                });
        },

        unmarkPurchasedItemAsReserved({ item, stock, pivotID }) {
            this.updatePendingReservationPurchaseItemLoading({ item, value: true });
            this.updatePendingReservationPurchaseItemUnreserving({ item, value: true });

            return PointOfSalePurchaseApi.unmarkPurchasedItemAsReserved(item, stock, this.activePointOfSale, pivotID)
                .then(value => {
                    this.updatePendingReservationPurchaseItem(value);
                })
                .catch(err => {
                    throw err;
                })
                .finally(() => {
                    this.updatePendingReservationPurchaseItemLoading({ item, value: false });
                    this.updatePendingReservationPurchaseItemUnreserving({ item, value: false });
                });
        },

        markPurchasedItemAsMissing({ item, stock, pivotID }) {
            this.updatePendingReservationPurchaseItemLoading({ item, value: true });
            this.updatePendingReservationPurchaseItemMissing({ item, value: true });

            return PointOfSalePurchaseApi.markPurchasedItemAsMissing(item, stock, this.activePointOfSale, pivotID)
                .then(value => {
                    this.updatePendingReservationPurchaseItem(value);
                })
                .catch(err => {
                    throw err;
                })
                .finally(() => {
                    this.updatePendingReservationPurchaseItemLoading({ item, value: false });
                    this.updatePendingReservationPurchaseItemMissing({ item, value: false });
                });
        },

        unmarkPurchasedItemAsMissing({ item, stock, pivotID }) {
            this.updatePendingReservationPurchaseItemLoading({ item, value: true });
            this.updatePendingReservationPurchaseItemUnmissing({ item, value: true });

            return PointOfSalePurchaseApi.unmarkPurchasedItemAsMissing(item, stock, this.activePointOfSale, pivotID)
                .then(value => {
                    this.updatePendingReservationPurchaseItem(value);
                })
                .catch(err => {
                    throw err;
                })
                .finally(() => {
                    this.updatePendingReservationPurchaseItemLoading({ item, value: false });
                    this.updatePendingReservationPurchaseItemUnmissing({ item, value: false });
                });
        },

        fetchNumberOfPendingArticleShippings({ pointOfSaleID, stockID }) {
            return PointOfSalePurchaseApi.fetchNumberOfPendingArticleShippings(pointOfSaleID, stockID, _.get(useSetupStore(pinia), 'activeCms.id', null))
                .then(value => {
                    this.updateNumberOfPendingArticleShippings(value);
                })
                .catch(err => {
                    throw err;
                });
        },

        fetchPendingShippingPurchases({ loading, pointOfSaleID, stockID, skip, take }) {
            if (loading !== false) this.updatePendingShippingPurchasesLoading(true);
            return PointOfSalePurchaseApi.fetchPendingShippingPurchases(pointOfSaleID, stockID, skip, take, _.get(useSetupStore(pinia), 'activeCms.id', null))
                .then(value => {
                    this.updatePendingShippingPurchases(value);
                })
                .catch(err => {
                    throw err;
                })
                .finally(() => {
                    if (loading !== false) this.updatePendingShippingPurchasesLoading(false);
                });
        },

        fetchPendingShippingPurchase({ loading, id, pointOfSaleID, stock }) {
            const purchase = this.pendingShippingPurchases.items.find(el => el.id === id);
            if (purchase && loading !== false) this.updatePendingShippingPurchaseLoading({ purchase, value: true });
            return PointOfSalePurchaseApi.fetchPendingShippingPurchase(id, pointOfSaleID, stock)
                .then(fetchedPurchase => {
                    this.updatePendingShippingPurchase(fetchedPurchase);
                })
                .catch(err => {
                    throw err;
                })
                .finally(() => {
                    if (purchase && loading !== false) this.updatePendingShippingPurchaseLoading({ purchase, value: false });
                });
        },

        createShippingPackage(shippingPackage) {
            this.updatePendingShippingPurchaseLoading({ purchase: shippingPackage.purchase, value: true });
            return ShippingPackageApi.create(shippingPackage)
                .then(createdShippingPackage => {
                    const purchase = this.pendingShippingPurchases.items.find(el => el.id === createdShippingPackage.purchase.id);
                    if (purchase) {
                        this.fetchPendingShippingPurchase({ id: purchase.id, pointOfSaleID: this.activePointOfSale.id })
                            .finally(() => {
                                this.updatePendingShippingPurchaseLoading({ purchase: createdShippingPackage.purchase, value: false });
                            });
                    }
                    return createdShippingPackage;
                })
                .catch(err => {
                    throw err;
                })
                .finally(() => {
                    this.updatePendingShippingPurchaseLoading({ purchase: shippingPackage.purchase, value: false });
                });
        },

        updateShippingPackage(shippingPackage) {
            this.updatePendingShippingPurchaseLoading({ purchase: shippingPackage.purchase, value: true });
            return ShippingPackageApi.update(shippingPackage)
                .then(updatedShippingPackage => {
                    const purchase = this.pendingShippingPurchases.items.find(el => el.id === updatedShippingPackage.purchase.id);
                    if (purchase) {
                        this.fetchPendingShippingPurchase({ id: purchase.id, pointOfSaleID: this.activePointOfSale.id })
                            .finally(() => {
                                this.updatePendingShippingPurchaseLoading({ purchase: updatedShippingPackage.purchase, value: false });
                            });
                    }
                    return updatedShippingPackage;
                })
                .catch(err => {
                    this.updatePendingShippingPurchaseLoading({ purchase: shippingPackage.purchase, value: false });
                    throw err;
                });
        },
    }
});
